import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showAlert, hideAlert } from './alertSlice';
import UploadService from '@/Service/UploadService';
import ChunkedUploadService, { reserveSlotForRehydrated } from '@/Service/ChunkedUploadService';
import ClipService from '@/Service/ClipService';
import { claimUploadSession as claimUploadSessionApi, fetchActiveSessions as fetchActiveSessionsApi, fetchPackageMetadata as fetchPackageMetadataApi } from '@/Service/UploadSessionService';
import { visibleRemoteUploads } from '@/components/UploadProgress/shouldFastPollFromState';
import { normalizeSourceFiles } from '@/utils/sourceFiles';
import { fileFingerprint, fileFromHandle, getFsHandle, hasAllFsHandles, removePendingPackage, savePendingPackage } from '@/utils/uploadPersistence';
import { reconcileZombieFailedToPaused } from './reconcileZombieUpload';
import { synthesizeLocalCardsFromBackend } from './synthesizeLocalCardsFromBackend';
import { pendingCancelIdSet } from '@/Service/pendingCancelQueue';

// Module-scoped memory for the raw payload of an upload that was just blocked by the 409
// guard. Files can't go in Redux state (non-serializable), so we stash the whole rawPayload
// here and consume it if the user clicks Take Over. Cleared when the dialog closes either way.
let pendingAttemptedUpload = null;

// Backend originalStatus values that mean "this upload is done, the rest is server-side
// pipeline" — when a resume errors and verify returns one of these, the UI should treat the
// upload as COMPLETED, not FAILED, and drop the IDB record so it doesn't reappear on reload.
const BACKEND_ORIGINAL_STATUS_DONE = new Set([
  'READY_TO_UPLOAD_TO_PROVIDER',
  'IN_PROGRESS_UPLOAD_TO_PROVIDER',
  'READY_FOR_CAT_DV',
  'CAT_DV_INGESTED',
  'UPLOADED',
  'EDITED',
  'TAGGED',
  'PUBLISHED',
  // Processing is paused; the upload itself is done, so this is COMPLETED not FAILED.
  'ON_HOLD',
]);

export const UploadClientStatus = Object.freeze({
  UPLOADING: 'UPLOADING',
  PAUSED: 'PAUSED',
  VERIFYING: 'VERIFYING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
});

/**
 * Upload state machine (client-side):
 * - `UPLOADING`: optimistic state immediately after dispatch; progress updates keep it here.
 * - `COMPLETED`: server responded successfully; nothing else changes.
 * - `VERIFYING`: transport-level issue (timeout/network/abort) - we haven't confirmed failure yet.
 * - `FAILED`: server responded with an actual error (HTTP response available).
 * - `CANCELED`: user cancelled the upload.
 *
 * Upload idempotency: Each logical upload attempt generates a stable uploadId. Retries reuse
 * the same uploadId so the backend can confirm whether the package already exists rather than
 * minting a duplicate. Only starting a brand-new upload re-generates the UUID.
 */

const isValidUUID = (uuid) => {
  const regex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return regex.test(uuid);
};

const shouldVerifyAfterError = (error) => {
  if (!error) {
    return true;
  }
  if (error.code === 'AUTH_401' || error.response?.status === 401) {
    return false;
  }
  if (error.name === 'AbortError') {
    return false;
  }
  return !error.response;
};

const getUploadErrorMessage = (error) => {
  if (error?.code === 'AUTH_401' || error?.response?.status === 401) {
    return 'Your session expired. Please sign in again and retry the upload.';
  }
  if (error?.code === 'AUTH_REQUIRED') {
    return 'Please sign in and retry the upload.';
  }
  return error?.userMessage || error?.message;
};

const logUploadClientEvent = (event, payload = {}) => {
  try {
    console.info(`[uploadClient] ${event}`, payload);
  } catch (e) {
    // noop in environments without console
  }
};

const mapVerificationState = (state) => {
  switch (state) {
    case 'RECEIVED':
      return {
        status: UploadClientStatus.UPLOADING,
        message: 'Upload reached the server and is waiting to process.',
      };
    case 'PROCESSING':
      return {
        status: UploadClientStatus.UPLOADING,
        message: 'Upload is still processing on the server.',
      };
    case 'COMPLETED':
      return {
        status: UploadClientStatus.COMPLETED,
        message: 'Upload already completed on the server.',
      };
    case 'CANCELLED':
      return {
        status: UploadClientStatus.CANCELED,
        message: 'Upload was canceled on the server.',
      };
    case 'FAILED':
      return {
        status: UploadClientStatus.FAILED,
        message: 'Server reported a failure. Please retry.',
      };
    case 'NOT_FOUND':
    default:
      return {
        status: UploadClientStatus.FAILED,
        message: 'Upload not found on the server. Please retry.',
      };
  }
};

const VERIFICATION_FAILURE_MESSAGE =
  'We could not confirm the result yet. Please refresh to check status.';

const UPLOAD_ALREADY_RECEIVED_MESSAGE =
  'This upload was already received. No duplicate package was created.';

const didReuseExistingUpload = (metadata) => {
  if (!metadata || !metadata.status) {
    return false;
  }
  return metadata.status !== 'READY_TO_UPLOAD_TO_PROVIDER';
};

export const fetchPackages = createAsyncThunk(
  'packages/fetchPackages',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      dispatch(hideAlert());
      const response = await UploadService.getFiles(params);
      return response.data;
    } catch (error) {
      dispatch(showAlert("error", "There was an error while fetching the uploads."))
      console.error("Error fetching data:", error);
      return rejectWithValue(error);
    }
  }
)

export const verifyUploadStatus = createAsyncThunk(
  'packages/verifyUploadStatus',
  async (uploadId, { rejectWithValue }) => {
    try {
      const response = await UploadService.verifyUploadStatus(uploadId);
      return { uploadId, data: response.data };
    } catch (error) {
      return rejectWithValue({ uploadId, error });
    }
  }
);

// crypto.randomUUID() exists only in a secure context (https:// or localhost). Served over
// http://<ip>:8080 (a DEV box without a certificate) it is undefined, and calling it threw before
// the try block below — the Upload button then did nothing: no request, no console error.
// crypto.getRandomValues is available on every origin, so fall back to an RFC 4122 v4 built from it.
const generateUploadId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex = Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const uploadPackage = createAsyncThunk(
  'packages/uploadPackage',
  async (rawPayload, { dispatch, rejectWithValue }) => {
    const uploadId = generateUploadId();

    const payload = { ...rawPayload, uploadMetadata: { ...rawPayload.uploadMetadata, uploadId } };
    try {
      dispatch(createUploadingPackage({ ...payload, uploadId }));
      dispatch(setIsProgressDropdownOpen(true));
      const response = await UploadService.upload(
        payload,
        (progress, remainingTime) => {
          dispatch(updateUploadingPackage({ uploadId, progress, remainingTime }));
        },
      );
      const reusedExisting = didReuseExistingUpload(response);
      logUploadClientEvent(reusedExisting ? 'upload-completed-existing' : 'upload-completed-new', { uploadId });
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.COMPLETED,
        statusMessage: reusedExisting ? UPLOAD_ALREADY_RECEIVED_MESSAGE : null,
      }));
      return response;
    } catch (error) {
      console.warn("[packagesSlice] uploadPackage error", { uploadId, message: error?.message, code: error?.code });
      // Single-active-upload guard fired on the server (cross-session — same-session second
      // POSTs pass the guard). Stash the attempted payload so Take Over can re-fire it
      // behind the claimed upload; open the takeover dialog to give the user context. Cancel
      // clears the stash so nothing is queued.
      if (error?.code === 'UPLOAD_ACTIVE_ELSEWHERE' && error?.conflict) {
        logUploadClientEvent('upload-blocked-conflict', {
          uploadId,
          existingUploadId: error.conflict.existingUploadId,
          existingStatus: error.conflict.existingStatus,
        });
        dispatch(deleteUploadingPackage(uploadId));
        pendingAttemptedUpload = rawPayload;
        dispatch(openUploadConflict({ conflict: error.conflict }));
        dispatch(fetchActiveSessions());
        return rejectWithValue(error);
      }
      // Another device took over ownership of the row mid-flight (TUS PATCH 423). Drop this
      // browser's local card entirely — the cross-device card list will surface the same
      // upload as a spectator entry (driven by /sessions/active), and having both visible was
      // producing duplicate rows on the "Active uploads" page. Also wipe the IndexedDB stub
      // so a page reload doesn't rehydrate a ghost local entry alongside the cross-device card.
      if (error?.code === 'UPLOAD_TAKEN_OVER') {
        logUploadClientEvent('upload-taken-over', { uploadId });
        dispatch(deleteUploadingPackage(uploadId));
        removePendingPackage(uploadId).catch(() => {});
        dispatch(fetchActiveSessions());
        return rejectWithValue(error);
      }
      // Auto-pause on transient tus errors (bug 2026-08). ChunkedUploadService
      // sets this code when the tus error is recoverable (network / 5xx / 408
      // / 401) — the client already ran pauseInFlight() to notify the backend
      // and update per-file state; the Redux status just needs to reflect
      // PAUSED instead of the usual FAILED so the card stays visible and
      // resumable. See classifyTusError.js for the exact rule.
      if (error?.code === 'AUTO_PAUSED_ON_ERROR') {
        logUploadClientEvent('upload-auto-paused-on-error', {
          uploadId,
          reason: error?.reason,
          message: error?.message,
        });
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.PAUSED,
          pauseReason: 'AUTO',
          // pausedAt drives the popup gate (decideShowAutoPausedPopup):
          // no popup until the upload has been auto-paused for >= 60s so
          // the silent auto-resume path gets its shot first during
          // transient dips.
          pausedAt: Date.now(),
          statusMessage: null,
        }));
        return rejectWithValue(error);
      }
      const uploadErrorMessage = getUploadErrorMessage(error);
      const fallbackStatus = shouldVerifyAfterError(error)
        ? UploadClientStatus.VERIFYING
        : UploadClientStatus.FAILED;
      if (fallbackStatus === UploadClientStatus.VERIFYING) {
        logUploadClientEvent('transport-error-verifying', { uploadId, code: error?.code, message: error?.message });
      } else {
        logUploadClientEvent('transport-error-failed', { uploadId, code: error?.code, message: error?.message });
      }
      dispatch(updateUploadingPackage({
        uploadId,
        status: fallbackStatus,
        error: uploadErrorMessage,
        verificationAttempted: fallbackStatus === UploadClientStatus.VERIFYING,
        statusMessage: fallbackStatus === UploadClientStatus.VERIFYING
          ? 'Connection interrupted. Checking server status...'
          : uploadErrorMessage,
      }));
      if (fallbackStatus === UploadClientStatus.FAILED && uploadErrorMessage) {
        dispatch(showAlert('error', uploadErrorMessage));
      }
      if (fallbackStatus === UploadClientStatus.VERIFYING) {
        dispatch(verifyUploadStatus(uploadId));
      }
      return rejectWithValue(error);
    }
  }
)

// Fetch the contributor's cross-device upload set from the server. Called on boot (after
// IndexedDB rehydration) and on tab-visibility wake so the resume UI can surface uploads that
// are still in flight (or paused) on another device / browser session. Populates
// `crossDeviceUploads` on the slice; `ownedByCurrentSession` distinguishes local vs remote.
//
// Second responsibility: detect ownership *loss*. If a locally-uploading package now appears
// with ownedByCurrentSession=false, another device claimed it via /sessions/{id}/claim while
// this device was mid-upload. That case is normally caught by the TUS PATCH 423 guard, but if
// this device has no PATCH in flight at the moment of takeover (e.g. it's parked in
// waitForBackendUploadReady after finalising bytes), the 423 never fires and the card would
// stay stuck showing "uploading". Pause the local session and flip to spectator here.
export const fetchActiveSessions = createAsyncThunk(
  'packages/fetchActiveSessions',
  async (_arg, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await fetchActiveSessionsApi();
      try {
        const local = (getState().packages.uploadingPackages || []);
        const remoteByUploadId = new Map(
          (response?.uploads || []).map((u) => [u.uploadId, u])
        );
        // Snapshot every poll's inputs so we can trace exactly why a card either does or
        // doesn't get pruned. Under normal steady-state uploads this fires every 2 s while a
        // card is visible; if it stops firing when a stale card is on-screen, the watcher's
        // shouldFastPoll condition is the problem. If it fires but skips the delete branch,
        // the remote/ownership shape is the problem.
        console.info('[UPLOAD-DIAG] fetchActiveSessions.detection', {
          localCount: local.length,
          remoteCount: (response?.uploads || []).length,
          currentSessionId: response?.currentSessionId,
          localSnapshot: local.map((u) => ({
            uploadId: u.uploadId,
            status: u.status,
            progress: u.progress,
            processingOnBackend: !!u.processingOnBackend,
            rehydrated: !!u.rehydrated,
          })),
          remoteSnapshot: (response?.uploads || []).map((u) => ({
            uploadId: u.uploadId,
            status: u.status,
            ownedByCurrentSession: u.ownedByCurrentSession,
            ownerSessionId: u.ownerSessionId,
          })),
        });
        for (const localUpload of local) {
          const remote = remoteByUploadId.get(localUpload.uploadId);

          // Zombie-upload reconciliation (bug 2026-08): tus.onError silently
          // marks local as FAILED but the backend row stays alive, and both
          // the local-card render and cross-device dedupe hide the upload
          // from the user — invisible and unresumable. If backend still
          // returns the row as owned by us, auto-promote back to PAUSED so
          // the card reappears with a Resume button. See
          // reconcileZombieUpload.js for the pure decision fn + tests.
          if (reconcileZombieFailedToPaused(localUpload, remote)) {
            console.info('[UPLOAD-DIAG] zombie-failed-reconciled', {
              uploadId: localUpload.uploadId,
              remoteStatus: remote.status,
              remoteBytesUploaded: remote.bytesUploaded,
            });
            logUploadClientEvent('zombie-failed-reconciled', {
              uploadId: localUpload.uploadId,
              remoteStatus: remote.status,
            });
            dispatch(updateUploadingPackage({
              uploadId: localUpload.uploadId,
              status: UploadClientStatus.PAUSED,
              pauseReason: 'AUTO',
              statusMessage: null,
            }));
            continue;
          }

          if (localUpload.status !== UploadClientStatus.UPLOADING
              && localUpload.status !== UploadClientStatus.PAUSED) continue;

          if (!remote) {
            // Server no longer reports this row (past ACTIVE_STATUSES — UPLOADED / FAILED /
            // CANCELLED / PUBLISHED — or hard-deleted). A rehydrated / processing-on-backend
            // card was speculatively surfaced from IDB after we lost the live SSE watcher
            // (page reload, cross-device takeover, etc). Without this branch, the card sits
            // "server finalizing" until watchPackageBackendStatus's own polling converges,
            // which was the 1-min UX regression reported after cross-device takeover.
            if (localUpload.processingOnBackend || localUpload.rehydrated) {
              console.info('[UPLOAD-DIAG] local-card-stale-remote-gone', {
                uploadId: localUpload.uploadId,
                localStatus: localUpload.status,
                processingOnBackend: !!localUpload.processingOnBackend,
                rehydrated: !!localUpload.rehydrated,
              });
              logUploadClientEvent('local-card-stale-remote-gone', {
                uploadId: localUpload.uploadId,
              });
              // Full teardown so a subsequent take-over doesn't get blocked by a leaked
              // sessionRecord / stale activePackageId. See releaseOwnershipLostSession.
              try { ChunkedUploadService.releaseOwnershipLostSession(localUpload.uploadId); } catch {}
              dispatch(deleteUploadingPackage(localUpload.uploadId));
              removePendingPackage(localUpload.uploadId).catch(() => {});
            }
            continue;
          }

          if (remote.ownedByCurrentSession) {
            // Redux says UPLOADING but this tab's tus session is parked on the resume gate
            // (and the server row typically reads PAUSED). Re-sync so the Resume button and
            // AutoResumeWatcher — both keyed on Redux PAUSED — can act. Seen 2026-09: card
            // stuck "paused", Resume click a silent no-op, /sessions/active PAUSED forever.
            if (localUpload.status === UploadClientStatus.UPLOADING
                && UploadService.isPaused(localUpload.uploadId)) {
              console.info('[UPLOAD-DIAG] local-uploading-inflight-paused-reconciled', {
                uploadId: localUpload.uploadId,
                remoteStatus: remote.status,
              });
              logUploadClientEvent('local-uploading-inflight-paused-reconciled', {
                uploadId: localUpload.uploadId,
                remoteStatus: remote.status,
              });
              dispatch(updateUploadingPackage({
                uploadId: localUpload.uploadId,
                status: UploadClientStatus.PAUSED,
                pauseReason: 'AUTO',
                pausedAt: Date.now(),
                statusMessage: null,
              }));
            }
            continue;
          }

          // We had the row locally and were uploading; server says another session owns it now.
          // Stop the tus loop, then drop our local card + IDB — the cross-device card list is
          // already surfacing the same upload as a spectator entry, so keeping a local copy
          // just produces duplicated rows on the Active uploads page.
          console.info('[UPLOAD-DIAG] ownership-lost-via-poll', {
            uploadId: localUpload.uploadId,
            localStatus: localUpload.status,
            remoteOwner: remote.ownerSessionId,
            remoteStatus: remote.status,
            remoteBytesUploaded: remote.bytesUploaded,
          });
          logUploadClientEvent('upload-ownership-lost-via-poll', {
            uploadId: localUpload.uploadId,
            remoteOwner: remote.ownerSessionId,
          });
          // Full teardown so a subsequent take-back doesn't hit an "Upload is already
          // running in another tab" from a leaked Web Lock, and doesn't queue behind a
          // stale activePackageId. See releaseOwnershipLostSession.
          try { ChunkedUploadService.releaseOwnershipLostSession(localUpload.uploadId); } catch {}
          dispatch(deleteUploadingPackage(localUpload.uploadId));
          removePendingPackage(localUpload.uploadId).catch(() => {});
        }
      } catch (err) {
        console.warn('[packagesSlice] ownership-loss detection failed', err?.message);
      }
      return response;
    } catch (error) {
      console.warn('[packagesSlice] fetchActiveSessions failed', {
        message: error?.message,
        status: error?.response?.status,
      });
      return rejectWithValue(error);
    }
  }
);

// Transfer ownership of a running/paused upload to this browser. After the server updates the
// row's session_id: (1) fetch the package's expected-files metadata (this browser doesn't have
// it in IDB because it wasn't the original uploader), (2) persist that as a paused stub so the
// existing rehydration flow surfaces a re-pick card, (3) close the takeover dialog. The
// previously-owning device's next TUS write will 423 or its next /sessions/active poll will
// flip it to spectator via the ownership-loss detection above.
// Per-upload claim work: server claim + fetch metadata + persist to IDB + rehydrate card +
// reserve queue slot. Extracted from claimUploadSession so the bulk take-over path
// (claimAllVisibleUploads) can iterate without duplicating this logic. Non-thunk: caller
// provides the dispatch.
const claimOne = async (uploadId, dispatch) => {
  const result = await claimUploadSessionApi(uploadId);
  logUploadClientEvent('upload-session-claimed', {
    uploadId,
    previousOwnerSessionId: result?.previousOwnerSessionId,
  });

  let metadata = null;
  try {
    metadata = await fetchPackageMetadataApi(uploadId);
  } catch (metadataError) {
    console.warn('[packagesSlice] fetchPackageMetadata after claim failed', {
      uploadId,
      message: metadataError?.message,
    });
  }
  if (metadata && Array.isArray(metadata.expectedFiles)) {
    const uploadedCount = metadata.expectedFiles.filter((f) => f.chunkStatus === 'UPLOADED').length;
    const partialCount = metadata.expectedFiles.filter((f) => f.chunkStatus !== 'UPLOADED' && (f.bytesUploaded || 0) > 0).length;
    const untouchedCount = metadata.expectedFiles.filter((f) => f.chunkStatus !== 'UPLOADED' && (f.bytesUploaded || 0) === 0).length;
    console.info('[UPLOAD-DIAG] claim/fetchMetadata', {
      uploadId,
      totalFiles: metadata.expectedFiles.length,
      uploadedCount,
      partialCount,
      untouchedCount,
      rowStatus: metadata.status,
      byFile: metadata.expectedFiles.map((f) => ({
        sourceFileId: f.sourceFileId,
        name: f.originalName,
        chunkStatus: f.chunkStatus,
        bytesUploaded: f.bytesUploaded,
        sizeBytesExpected: f.sizeBytesExpected,
        hasTusUrl: !!f.tusUploadUrl,
      })),
    });
  }

  if (metadata && Array.isArray(metadata.expectedFiles)) {
    const totalSize = metadata.expectedFiles
      .reduce((acc, f) => acc + (f.sizeBytesExpected || 0), 0);
    const persistedRecord = {
      uploadId: metadata.uploadId,
      packageName: metadata.packageName,
      totalSize,
      createdAt: Date.now(),
      files: metadata.expectedFiles.map((expected) => ({
        sourceFileId: expected.sourceFileId,
        originalName: expected.originalName,
        sizeBytes: expected.sizeBytesExpected,
        bytesUploaded: expected.bytesUploaded || 0,
        // CRITICAL: propagate chunkStatus so resume()'s isFileAlreadyUploaded() correctly
        // filters out files that were already finalized on the source device. Without this
        // B tries to re-upload UPLOADED files, its tus-js-client HEADs their (possibly
        // already-cleaned-up) TUS sessions, gets an error, and gets stuck partway through.
        chunkStatus: expected.chunkStatus,
        fingerprint: null,
        fsHandle: null,
        tusEndpoint: expected.tusEndpoint,
        tusUploadUrl: expected.tusUploadUrl || null,
      })),
    };
    try {
      await savePendingPackage(persistedRecord);
    } catch (persistError) {
      console.warn('[packagesSlice] savePendingPackage after claim failed', {
        uploadId,
        message: persistError?.message,
      });
    }
    dispatch(rehydratePausedPackage(persistedRecord));
    // Reserve the queue slot on the claimed upload — a follow-up uploadPackage (from the
    // dialog's re-fire of pendingAttemptedUpload) then queues *behind* this reservation.
    reserveSlotForRehydrated(metadata.uploadId);
  }

  return result;
};

// Bulk take-over: claims every visible remote upload for the current contributor. This
// matches the "make this device the main one for this contributor" UX — the cross-device
// section has a single Take Over button now instead of per-card ones, and the conflict
// dialog's Take Over uses the same action.
export const claimAllVisibleUploads = createAsyncThunk(
  'packages/claimAllVisibleUploads',
  async (_arg, { dispatch, getState, rejectWithValue }) => {
    try {
      // Snapshot visible remote uploads at click time — the /sessions/active list can churn
      // during the claims, so freezing the list up front avoids racing a mid-flight refresh.
      const state = getState();
      const remoteItems = state?.packages?.crossDeviceUploads?.items || [];
      const targets = visibleRemoteUploads(remoteItems).map((u) => u.uploadId).filter(Boolean);
      logUploadClientEvent('upload-session-claim-all', { count: targets.length, uploadIds: targets });

      // Claim sequentially — each claim mutates server state (session_id UPDATE) and we want
      // deterministic ordering for the reserved-slot queue on the client.
      for (const uploadId of targets) {
        try {
          await claimOne(uploadId, dispatch);
        } catch (perUploadError) {
          console.warn('[packagesSlice] claimAllVisibleUploads: one upload failed', {
            uploadId,
            message: perUploadError?.message,
            status: perUploadError?.response?.status,
          });
        }
      }

      // Snapshot pendingAttemptedUpload before dispatching closeUploadConflict — see the
      // closeUploadConflict reducer, which clears pendingAttemptedUpload.
      const attempted = pendingAttemptedUpload;
      pendingAttemptedUpload = null;
      // Re-dispatch the blocked upload now that we hold reserved slots for every claimed
      // upload. It queues behind those reservations.
      if (attempted) {
        dispatch(uploadPackage(attempted));
      }

      // Hold the dialog + spinner open for one paint frame so the rehydrated cards land
      // before the dialog unmounts (no empty-UI flash).
      await new Promise((resolve) =>
        (typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame(() => resolve())
          : setTimeout(resolve, 0))
      );
      dispatch(closeUploadConflict());
      dispatch(fetchActiveSessions());
      return { claimed: targets.length };
    } catch (error) {
      console.warn('[packagesSlice] claimAllVisibleUploads failed', {
        message: error?.message,
      });
      dispatch(showAlert('error', 'Could not take over uploads. Please try again.'));
      return rejectWithValue(error);
    }
  }
);

// Backend-only card hydration (2026-08-05 follow-up). The synthesized PAUSED
// card from fetchActiveSessions.fulfilled shows the upload but has no files
// manifest, so the resume flow can't reconstruct the tus session. This thunk
// fetches the manifest from the backend and upgrades the card in place —
// writing an IDB record + re-dispatching rehydratePausedPackage so the card
// now has pausedRecord + reserved slot and behaves identically to a card
// rehydrated from IDB. Different from claimOne in packagesSlice: no
// claimUploadSession call, since ownedByCurrentSession is already true.
export const hydratePackageFromBackend = createAsyncThunk(
  'packages/hydratePackageFromBackend',
  async (uploadId, { dispatch, getState, rejectWithValue }) => {
    if (!uploadId) return null;
    let metadata = null;
    try {
      metadata = await fetchPackageMetadataApi(uploadId);
    } catch (err) {
      console.warn('[packagesSlice] hydratePackageFromBackend/fetchMetadata failed', {
        uploadId,
        message: err?.message,
        status: err?.response?.status,
      });
      return rejectWithValue(err?.message || 'fetchMetadata failed');
    }
    if (!metadata || !Array.isArray(metadata.expectedFiles)) return null;

    // Post-2026-08-06 diagnostic: after admin-takeover the resume flow was
    // reported starting from byte 0. Log what the backend actually returned
    // so we can distinguish (a) backend returned bytesUploaded=0 for the
    // admin from (b) IDB has correct offset but tus HEAD falls back to POST.
    // Keep the payload compact — one line per file.
    // eslint-disable-next-line no-console
    console.info('[UPLOAD-DIAG] hydratePackageFromBackend/metadata', {
      uploadId,
      packageName: metadata.packageName,
      status: metadata.status,
      files: metadata.expectedFiles.map((f) => ({
        sourceFileId: f.sourceFileId,
        name: f.originalName,
        sizeBytes: f.sizeBytesExpected,
        bytesUploaded: f.bytesUploaded || 0,
        chunkStatus: f.chunkStatus,
        hasTusUrl: !!f.tusUploadUrl,
        tusUrlTail: f.tusUploadUrl ? f.tusUploadUrl.split('/').pop() : null,
      })),
    });

    // Preserve the original contributor attribution across the hydrate step.
    // fetchPackageMetadata doesn't return contributorName, so we look it up
    // from the cross-device items already in Redux (the same fetch that
    // triggered this hydration) — that response now carries contributorId
    // and contributorName post-2026-08-06 backend fix.
    const crossDeviceItem = (getState().packages?.crossDeviceUploads?.items || [])
      .find((u) => u?.uploadId === uploadId) || null;

    const totalSize = metadata.expectedFiles
      .reduce((acc, f) => acc + (f.sizeBytesExpected || 0), 0);
    const persistedRecord = {
      uploadId: metadata.uploadId || uploadId,
      packageName: metadata.packageName,
      totalSize,
      createdAt: Date.now(),
      contributorId: crossDeviceItem?.contributorId || null,
      contributorName: crossDeviceItem?.contributorName || null,
      files: metadata.expectedFiles.map((expected) => ({
        sourceFileId: expected.sourceFileId,
        originalName: expected.originalName,
        sizeBytes: expected.sizeBytesExpected,
        bytesUploaded: expected.bytesUploaded || 0,
        chunkStatus: expected.chunkStatus,
        fingerprint: null,
        fsHandle: null,
        tusEndpoint: expected.tusEndpoint,
        tusUploadUrl: expected.tusUploadUrl || null,
      })),
    };
    try {
      await savePendingPackage(persistedRecord);
    } catch (err) {
      console.warn('[packagesSlice] hydratePackageFromBackend/persist failed', {
        uploadId,
        message: err?.message,
      });
    }
    // rehydratePausedPackage's reducer replaces an existing entry (the
    // synthesized backend-only card) if the current status is recoverable.
    // PAUSED IS explicitly guarded from replacement above, so we bypass by
    // dispatching a temporary FAILED status shim first — actually the
    // reducer's guard set is (FAILED, VERIFYING) so a fromBackendOnly PAUSED
    // entry won't be replaced. Delete it first, then rehydrate.
    dispatch(deleteUploadingPackage(uploadId));
    dispatch(rehydratePausedPackage(persistedRecord));
    reserveSlotForRehydrated(uploadId);
    return persistedRecord;
  }
);

export const claimUploadSession = createAsyncThunk(
  'packages/claimUploadSession',
  async (uploadId, { dispatch, rejectWithValue }) => {
    try {
      const result = await claimOne(uploadId, dispatch);

      // Snapshot pendingAttemptedUpload BEFORE the closeUploadConflict dispatch below — the
      // reducer for that action also clears pendingAttemptedUpload, so reading it after the
      // dispatch would always get null and the re-fire would never run.
      const attempted = pendingAttemptedUpload;
      pendingAttemptedUpload = null;

      // Re-dispatch the blocked upload now that we hold the queue slot for the claimed
      // upload. It queues behind the reservation and starts automatically when the user
      // resumes the claimed upload and it completes.
      if (attempted) {
        dispatch(uploadPackage(attempted));
      }

      // Hold the dialog + its spinner open for one paint frame so the just-dispatched
      // rehydratePausedPackage (and, if any, uploadPackage optimistic card) is on-screen
      // BEFORE the dialog unmounts. Without this, users see a ~100ms flash of empty UI
      // between the dialog closing and React painting the new cards.
      await new Promise((resolve) =>
        (typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame(() => resolve())
          : setTimeout(resolve, 0))
      );
      dispatch(closeUploadConflict());

      dispatch(fetchActiveSessions());
      return result;
    } catch (error) {
      console.warn('[packagesSlice] claimUploadSession failed', {
        uploadId,
        message: error?.message,
        status: error?.response?.status,
      });
      dispatch(showAlert('error', 'Could not take over the upload. Please try again.'));
      return rejectWithValue(error);
    }
  }
);

export const retryUploadPackage = createAsyncThunk(
  'packages/retryUploadPackage',
  async (uploadId, { dispatch, rejectWithValue, getState }) => {
    const uploadingPackages = getState().packages.uploadingPackages;
    const previousPayload = uploadingPackages.find((p) => p.uploadId === uploadId);
    if (!previousPayload) {
      console.error("No previous payload found for the given uploadId");
      return;
    }
    const { files, uploadMetadata } = previousPayload;

    // Before kicking off a fresh attempt, ask the server what state the upload is actually in.
    // After a sleep wake (Bug 1) the bytes that flew before sleep may have all made it through —
    // the server then sees the upload as COMPLETED while the client only remembers the FAILED
    // state from the dropped retry-tail. Verifying first turns those into a clean COMPLETED
    // instead of a duplicated upload attempt that crashes on 409 from /packages init.
    try {
      const verifyResponse = await UploadService.verifyUploadStatus(uploadId);
      const verified = mapVerificationState(verifyResponse?.data?.state);
      if (verified.status === UploadClientStatus.COMPLETED) {
        logUploadClientEvent('retry-resolved-via-verify-completed', { uploadId });
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.COMPLETED,
          statusMessage: verified.message,
        }));
        return { uploadId, completedViaVerify: true };
      }
      if (verified.status === UploadClientStatus.CANCELED) {
        logUploadClientEvent('retry-resolved-via-verify-cancelled', { uploadId });
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.CANCELED,
          statusMessage: verified.message,
        }));
        return { uploadId, cancelled: true };
      }
      if (verified.status === UploadClientStatus.FAILED) {
        logUploadClientEvent('retry-blocked-by-server-failed', { uploadId });
        // Server already considers this upload dead. A fresh attempt with the same uploadId
        // will 409 on /packages init (PackageInitService.RESUMABLE_STATUSES excludes FAILED).
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.FAILED,
          error: verified.message,
          statusMessage: 'This upload session expired on the server. Please start a new upload from the form.',
        }));
        dispatch(showAlert('error', 'Upload session expired. Please start a new upload.'));
        return rejectWithValue({ uploadId, reason: 'server_session_expired' });
      }
      // RECEIVED / PROCESSING fall through to the retry attempt — the server still considers
      // the upload alive and resumable.
    } catch (verifyError) {
      // Verify itself failing is non-fatal: just continue with the retry attempt. Logging it
      // separately so a regression in /metadata/status doesn't get masked by a retry failure.
      console.warn("[packagesSlice] retryUploadPackage verify failed, continuing with retry", {
        uploadId,
        message: verifyError?.message,
      });
    }

    // If the cached File[] objects are gone (e.g. the user reloaded the page after the failure
    // landed) we can't ship bytes — the browser can't materialise File handles from Redux state
    // across a reload. Tell the user to use the dropzone instead of silently failing.
    const hasUsableFiles = Array.isArray(files)
      && files.length > 0
      && files.every((f) => f && typeof f === 'object' && typeof f.size === 'number');
    if (!hasUsableFiles) {
      const message = 'Please select the same files used for this upload.';
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.FAILED,
        error: message,
        statusMessage: message,
      }));
      dispatch(showAlert('error', message));
      return rejectWithValue({ uploadId, reason: 'files_unavailable' });
    }

    const payload = { files, uploadMetadata };
    try {
      dispatch(setIsProgressDropdownOpen(true));
      dispatch(updateUploadingPackage({ uploadId, status: UploadClientStatus.UPLOADING }));
      const response = await UploadService.upload(
        payload,
        (progress, remainingTime) => {
          dispatch(updateUploadingPackage({ uploadId, progress, remainingTime }));
        },
      );
      const reusedExisting = didReuseExistingUpload(response);
      logUploadClientEvent(reusedExisting ? 'upload-completed-existing' : 'upload-completed-new', { uploadId });
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.COMPLETED,
        statusMessage: reusedExisting ? UPLOAD_ALREADY_RECEIVED_MESSAGE : null,
      }));
      return response;
    } catch (error) {
      console.warn("[packagesSlice] retryUploadPackage error", { uploadId, message: error?.message, code: error?.code });
      // /packages init returns 409 when the row is in a non-resumable state (FAILED, PUBLISHED,
      // CANCELLED, …). Surface a clear message instead of "Request failed with status 409".
      if (error?.response?.status === 409) {
        const message = 'This upload session expired on the server. Please start a new upload from the form.';
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.FAILED,
          error: message,
          statusMessage: message,
        }));
        dispatch(showAlert('error', message));
        return rejectWithValue({ uploadId, reason: 'session_expired_409' });
      }
      const uploadErrorMessage = getUploadErrorMessage(error);
      const fallbackStatus = shouldVerifyAfterError(error)
        ? UploadClientStatus.VERIFYING
        : UploadClientStatus.FAILED;
      if (fallbackStatus === UploadClientStatus.VERIFYING) {
        logUploadClientEvent('transport-error-verifying', { uploadId, code: error?.code, message: error?.message });
      } else {
        logUploadClientEvent('transport-error-failed', { uploadId, code: error?.code, message: error?.message });
      }
      dispatch(updateUploadingPackage({
        uploadId,
        status: fallbackStatus,
        error: uploadErrorMessage,
        verificationAttempted: fallbackStatus === UploadClientStatus.VERIFYING,
        statusMessage: fallbackStatus === UploadClientStatus.VERIFYING
          ? 'Connection interrupted. Checking server status...'
          : uploadErrorMessage,
      }));
      if (fallbackStatus === UploadClientStatus.FAILED && uploadErrorMessage) {
        dispatch(showAlert('error', uploadErrorMessage));
      }
      if (fallbackStatus === UploadClientStatus.VERIFYING) {
        dispatch(verifyUploadStatus(uploadId));
      }
      return rejectWithValue(error);
    }
  }
)

export const cancelUpload = createAsyncThunk(
  'packages/cancelUpload',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    // Back-compat: callers may still pass a bare uploadId string. The cancel dialog
    // passes { uploadId, reason, notes } — required reason is enforced server-side.
    const { uploadId, reason = null, notes = null } =
      typeof payload === 'string' ? { uploadId: payload } : (payload || {});
    if (!isValidUUID(uploadId)) {
      console.error("Invalid UUID format");
      return;
    } else {
      dispatch(updateUploadingPackage({ uploadId, status: UploadClientStatus.CANCELED }));
      await UploadService.cancel(uploadId, reason, notes);
    }
  }
);

export const pauseUpload = createAsyncThunk(
  'packages/pauseUpload',
  async (uploadId, { dispatch }) => {
    if (!isValidUUID(uploadId)) {
      console.error("Invalid UUID format");
      return false;
    }
    const ok = UploadService.pause(uploadId);
    if (ok) {
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.PAUSED,
        statusMessage: null,
      }));
      return true;
    }
    // Bug 3 (2026-08): the button used to be a silent no-op on very bad
    // network. pauseInFlight returned false (typically because the tus
    // session hadn't been added to the in-flight registry yet — the init
    // POST /packages was still on the wire) and the caller ignored the
    // return. Surface it: user learns the click was received but couldn't
    // apply, and can retry once init completes. Full reason is captured in
    // the upload's diagnostics timeline (see pauseInFlight for the record).
    dispatch(showAlert(
      "warning",
      "Couldn't pause yet — the upload is still starting up. Try again in a moment."
    ));
    return false;
  }
);

// Post-8-hour-sleep bug (2026-08-05): when a user clicks Resume after a long
// suspension, tus.Upload instances in the in-memory inFlight Map are stale
// (dead TCP sockets, retry counters at max) and calling .start() on them
// often triggers an immediate tus.onError → classifyTusError transient →
// notifyAutoPause → Redux flips back to PAUSED. From the user's POV: click
// Resume → spinner → back to Resume, no progress. A page reload recovers
// (fresh tus.Upload instances from IndexedDB rehydration).
//
// Track resume dispatches per uploadId so the notifyAutoPause bridge in
// store.js can detect the immediate flip-back pattern and surface a
// "please refresh the page" hint instead of leaving the user clicking
// Resume forever.
const RESUME_STALE_WINDOW_MS = 30 * 1000;
const recentResumeAttempts = new Map();

export const wasRecentlyResumed = (uploadId) => {
  if (!uploadId) return false;
  const at = recentResumeAttempts.get(uploadId);
  return typeof at === 'number' && Date.now() - at < RESUME_STALE_WINDOW_MS;
};

export const clearRecentResume = (uploadId) => {
  recentResumeAttempts.delete(uploadId);
};

// Full teardown of the module-scoped state that lives outside Redux (2026-08-05
// multi-tenancy leak fix). Called from the store middleware on
// auth/signOut/fulfilled, alongside the slice-level reset and
// ChunkedUploadService.abortAll(). Without this, the resume tracker and the
// stashed conflict payload survive logout and could poison the next user's
// resume/take-over flow.
export const __resetPackagesModuleStateOnSignOut = () => {
  recentResumeAttempts.clear();
  pendingAttemptedUpload = null;
};

// Accepts either a bare uploadId (user click) or `{ uploadId, silent: true }` — the
// AutoResumeWatcher's silent network-recovery retries. Silent resumes don't arm the
// "re-paused right after resume" toast in store.js: a flaky network re-pausing an
// automatic retry is expected and must not nag the user on every attempt.
export const resumeUpload = createAsyncThunk(
  'packages/resumeUpload',
  async (arg, { dispatch }) => {
    const uploadId = typeof arg === 'string' ? arg : arg?.uploadId;
    const silent = typeof arg === 'object' && !!arg?.silent;
    if (!isValidUUID(uploadId)) {
      console.error("Invalid UUID format");
      return false;
    }
    const ok = UploadService.resume(uploadId);
    if (ok) {
      if (!silent) recentResumeAttempts.set(uploadId, Date.now());
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.UPLOADING,
        statusMessage: null,
      }));
      return true;
    }
    // resumeInFlight returned false — the session isn't in the in-flight
    // registry. Most common cause: tab reloaded / evicted, package should
    // go through the rehydrated-resume flow (file re-pick) instead. Tell
    // the user rather than leaving Resume as a silent no-op (2026-08-05).
    logUploadClientEvent('upload-resume-refused-no-session', { uploadId });
    dispatch(showAlert(
      'warning',
      "Couldn't resume this upload from memory — please refresh the page to continue where you left off."
    ));
    return false;
  }
);

// Resume a paused upload that was rehydrated from IndexedDB after a page reload. Because the
// browser can't bring File objects back across a reload, the user has to re-select files in a
// picker — this thunk validates by fingerprint and then drives ChunkedUploadService.resume.
export const rehydratedResumeUpload = createAsyncThunk(
  'packages/rehydratedResumeUpload',
  async ({ uploadId, files, mode = "files" }, { dispatch, getState }) => {
    if (!isValidUUID(uploadId)) {
      console.error("Invalid UUID format");
      return false;
    }
    const upload = getState().packages.uploadingPackages.find((p) => p.uploadId === uploadId);
    const record = upload?.pausedRecord;
    if (!record) {
      console.error("No paused record for", uploadId);
      return false;
    }
    if (!files || files.length === 0) return false;
    const dropped = Array.from(files);
    const fpMap = new Map();
    dropped.forEach((f) => {
      const key = fileFingerprint(f);
      const matches = fpMap.get(key) || [];
      matches.push(f);
      fpMap.set(key, matches);
    });
    const expected = record.files || [];
    const fingerprintKeyForEntry = (entry) =>
      entry.fingerprint
        ? entry.fingerprint.split('|').slice(0, 2).join('|')
        : `${entry.originalName}|${entry.sizeBytes}`;
    const takeMatchedFiles = (entries) => {
      const remaining = new Map(
        Array.from(fpMap.entries()).map(([key, value]) => [key, [...value]])
      );
      return entries.map((entry) => {
        const candidates = remaining.get(fingerprintKeyForEntry(entry)) || [];
        return candidates.shift() || null;
      });
    };
    // Already-uploaded files don't need to be re-picked — the bytes are server-side, and
    // re-running them through the picker check would force the user to re-select files
    // that have nothing to do for the resume. The chunked uploader's resume() also skips
    // them on its own; keeping the missing-check in sync prevents a confusing "Missing N
    // file(s)" error when the user picks only the files that actually still need bytes.
    // Only trust the chunkStatus mark — see the matching comment on isFileAlreadyUploaded in
    // ChunkedUploadService.js. The bytes-based fallback would silently exclude files that
    // had their last onProgress fire before a pause/reload but whose onSuccess never landed.
    const stillPending = expected.filter((f) => f.chunkStatus !== "UPLOADED");
    const matchedPendingFiles = takeMatchedFiles(stillPending);
    const missing = stillPending.filter((_, index) => !matchedPendingFiles[index]);
    if (missing.length > 0) {
      dispatch(updateUploadingPackage({
        uploadId,
        statusMessage: mode === "folder"
          ? `Couldn't find ${missing.length} file(s) in that folder: ${missing.map((m) => m.originalName).join(", ")}. ` +
            "Try a different folder, or pick the files manually."
          : `Missing ${missing.length} file(s): ${missing.map((m) => m.originalName).join(", ")}. ` +
            "Please select the same files used for this upload.",
      }));
      return false;
    }
    // In folder mode the user may have selected a directory with hundreds of unrelated files —
    // hand the resume only the matched subset so we don't try to upload extras.
    const resumeFiles = mode === "folder"
      ? takeMatchedFiles(expected).filter(Boolean)
      : dropped;
    dispatch(updateUploadingPackage({
      uploadId,
      status: UploadClientStatus.UPLOADING,
      statusMessage: null,
      // Clear the rehydration marker so future Resume clicks (after another pause) go through
      // the normal in-flight resume path instead of opening the file picker again.
      rehydrated: false,
      pausedRecord: null,
    }));
    try {
      // Auto-chain any sibling rehydrated paused packages whose files are also reachable via
      // persisted FS handles. Fired BEFORE awaiting this package's resume so the siblings line
      // up in the global queue immediately, instead of waiting for this whole upload to finish.
      // Handles every entry path (saved-handles auto, folder picker, file picker) uniformly.
      dispatch(autoResumeRehydratedWithHandles(uploadId));
      // Re-stamp ownership before any TUS traffic. This path used to go
      // straight to TUS with whatever session id the browser currently has;
      // after a sign-out/re-login (which mints a new session id, see
      // clearUploadSessionIdOnSignOut) or an admin take-over, the first PATCH
      // hit the 423 ownership guard and the catch below deleted the card and
      // the IDB record — "resume does not work". The server allows the owner
      // to re-claim their own upload, and the claim is a no-op when the
      // session id already matches.
      try {
        await claimUploadSessionApi(uploadId);
      } catch (claimError) {
        // Non-fatal: a transient claim failure must not block the resume
        // attempt — if ownership genuinely sits elsewhere the TUS 423 path
        // below still handles it.
        console.warn('[rehydratedResumeUpload] pre-resume claim failed', {
          uploadId,
          status: claimError?.response?.status,
          message: claimError?.message,
        });
      }
      const response = await ChunkedUploadService.resume(
        record,
        resumeFiles,
        (progress, remainingTime) => {
          dispatch(updateUploadingPackage({ uploadId, progress, remainingTime }));
        }
      );
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.COMPLETED,
        statusMessage: null,
      }));
      return response;
    } catch (error) {
      // Cross-device takeover during a resume: another device just claimed this upload while
      // we were mid-flow. Drop this browser's local card + IDB entry so only the cross-device
      // card renders — same behaviour as uploadPackage's UPLOAD_TAKEN_OVER branch.
      if (error?.code === 'UPLOAD_TAKEN_OVER') {
        logUploadClientEvent('rehydrated-resume-taken-over', { uploadId });
        dispatch(deleteUploadingPackage(uploadId));
        removePendingPackage(uploadId).catch(() => {});
        dispatch(fetchActiveSessions());
        return false;
      }
      // Before surfacing a FAILED card, ask the backend what state the package is actually in.
      // A 409 from POST /api/v1/upload/tus/ ("Package … is not accepting more files") happens
      // when the package status has already advanced (e.g., bytes finalized while the user
      // was offline, or admin-cancelled). Without this reconciliation step the user lands in
      // a permanent loop: card→FAILED→reload→re-rehydrate→click Resume→409 again, because the
      // IDB record persists. After verification we either show success (if the upload actually
      // finished) or remove the IDB record so the dead row doesn't keep coming back.
      let resolvedAs = 'failed';
      let resolvedMessage = getUploadErrorMessage(error);
      try {
        const verifyResp = await UploadService.verifyUploadStatus(uploadId);
        const originalStatus = verifyResp?.data?.originalStatus || null;
        const state = verifyResp?.data?.state || null;
        if (originalStatus && BACKEND_ORIGINAL_STATUS_DONE.has(originalStatus)) {
          resolvedAs = 'completed';
        } else if (state === 'FAILED' || state === 'CANCELLED' || state === 'NOT_FOUND') {
          resolvedAs = 'terminal';
          resolvedMessage = state === 'CANCELLED'
            ? 'Upload was cancelled on the server.'
            : state === 'NOT_FOUND'
              ? 'Upload session is no longer available on the server. Please start a new upload.'
              : `Upload failed on the server (status=${originalStatus || 'unknown'}). Please start a new upload.`;
        }
      } catch (verifyError) {
        console.warn('[rehydratedResumeUpload] verify after resume error failed', {
          uploadId,
          message: verifyError?.message,
        });
      }
      if (resolvedAs === 'completed') {
        logUploadClientEvent('rehydrated-resume-resolved-via-verify-completed', { uploadId });
        dispatch(updateUploadingPackage({
          uploadId,
          status: UploadClientStatus.COMPLETED,
          statusMessage: 'Upload already completed on the server.',
        }));
        removePendingPackage(uploadId).catch(() => {});
        return true;
      }
      dispatch(updateUploadingPackage({
        uploadId,
        status: UploadClientStatus.FAILED,
        error: resolvedMessage,
        statusMessage: resolvedMessage,
      }));
      if (resolvedMessage) dispatch(showAlert('error', resolvedMessage));
      // For terminal backend states drop the IDB record so the next reload doesn't surface
      // the same dead row again. For non-terminal cases (transient network error, etc.) we
      // leave it in place so the user could retry by reloading.
      if (resolvedAs === 'terminal') {
        removePendingPackage(uploadId).catch(() => {});
      }
      return false;
    }
  }
);

// After the user resumes one rehydrated paused package via the saved FS-handle path, auto-queue
// any OTHER rehydrated paused packages whose files are also reachable via persisted handles. The
// user's mental model (per the queue UI) is "uploads run one after another", so leaving siblings
// frozen while the active one finishes contradicts that. Packages without handles (need a folder
// re-pick) stay paused — we can't kick them off without a user gesture for the picker.
// Global queue controls. The per-card pause/resume model didn't scale to many packages: the
// user kept asking "wait, which card pauses what?". One control pauses everything in flight,
// one resume restarts everything that can be auto-restarted (rehydrated packages that need a
// folder/file re-pick still have their own per-card buttons since each one needs a user gesture).
export const pauseAllUploads = createAsyncThunk(
  'packages/pauseAllUploads',
  async (_, { dispatch, getState }) => {
    const active = getState().packages.uploadingPackages.filter(
      (p) => p.status === UploadClientStatus.UPLOADING
    );
    for (const p of active) {
      dispatch(pauseUpload(p.uploadId));
    }
  }
);

export const resumeAllUploads = createAsyncThunk(
  'packages/resumeAllUploads',
  async (_, { dispatch, getState }) => {
    // Also pick up cards whose Redux status drifted to UPLOADING while the in-memory tus
    // session is actually parked on the resume gate (auto-pause after a user/auto resume).
    // The card renders as paused via telemetry in that state, so a Resume click must act.
    const paused = getState().packages.uploadingPackages.filter(
      (p) => p.status === UploadClientStatus.PAUSED
        || (p.status === UploadClientStatus.UPLOADING && UploadService.isPaused(p.uploadId))
    );
    paused
      .filter((p) => !p.rehydrated)
      .forEach((p) => dispatch(resumeUpload(p.uploadId)));
    // Rehydrated paused with persisted FS handles get auto-resumed in a batch. The autoResume
    // thunk skips ones without handles — those still need the per-card folder picker.
    dispatch(autoResumeRehydratedWithHandles(null));
  }
);

export const autoResumeRehydratedWithHandles = createAsyncThunk(
  'packages/autoResumeRehydratedWithHandles',
  async (excludeUploadId, { dispatch, getState }) => {
    const candidates = getState().packages.uploadingPackages
      .filter((p) => {
        if (p.uploadId === excludeUploadId) return false;
        if (p.status !== UploadClientStatus.PAUSED) return false;
        if (!p.rehydrated) return false;
        const sourceFileIds = (p.pausedRecord?.files || [])
          .map((f) => f.sourceFileId)
          .filter(Boolean);
        return sourceFileIds.length > 0 && hasAllFsHandles(p.uploadId, sourceFileIds);
      })
      // Dispatch chronologically so the queue order matches createdAt. Without the sort,
      // rehydratePausedPackage's reverse-chronological insertion order would auto-chain
      // newer packages ahead of older ones.
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    for (const pkg of candidates) {
      const entries = pkg.pausedRecord?.files || [];
      const resolved = await Promise.all(
        entries.map((f) =>
          fileFromHandle(getFsHandle(pkg.uploadId, f.sourceFileId), f.fingerprint)
        )
      );
      const files = resolved.map((r) => r?.file).filter(Boolean);
      if (files.length !== entries.length) {
        // A handle that worked at boot now fails (file moved/permission lost). Leave this one
        // paused — the per-card path will surface the right re-pick prompt on user click.
        continue;
      }
      dispatch(rehydratedResumeUpload({ uploadId: pkg.uploadId, files, mode: "auto" }));
    }
  }
);


export const fetchPackageDetails = createAsyncThunk(
  'packages/fetchPackageDetails',
  async (uploadMetadataId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(hideAlert());
      const response = await ClipService.getPackageDetails({ uploadMetadataId });
      return response.data;
    } catch (error) {
      dispatch(showAlert("error", "There was an error while fetching the upload details."))
      console.error("Error fetching data:", error);
      return rejectWithValue(error);
    }
  }
)

const enrichUpload = (upload = {}) => ({
  ...upload,
  sourceFiles: normalizeSourceFiles(upload),
});

const initialPackagesState = {
    packages: [],
    packagesView: 'list', // list | grid
    totalPages: 0,
    totalElements: 0,
    fetch: {
      status: 'idle', // idle | loading | succeeded | failed
      error: null,
    },
    isProgressDropdownOpen: false,
    uploadingPackages: [],
    // Tracks the initial IndexedDB-driven rehydration on page boot. Starts as 'pending' so
    // the InProgressUploadsPage (and any other consumer) can show a spinner instead of the
    // "no uploads" empty state while we're still reading IDB and verifying each package
    // against the backend. Flipped to 'done' by ResumePreviousUploadBanner once its first
    // loadPending pass settles. Subsequent visibility-wake rescans deliberately do NOT flip
    // this back — the page already has content and a flicker-to-spinner would be worse than
    // the silent rescan.
    rehydrationStatus: 'pending', // 'pending' | 'done'
    // Populated by fetchActiveSessions — the contributor's non-terminal uploads across all
    // devices. Distinct from `uploadingPackages` (local session's live cards) so the two views
    // can coexist without the boot-time IDB rehydration racing the network fetch.
    crossDeviceUploads: {
      status: 'idle', // idle | loading | succeeded | failed
      currentSessionId: null,
      items: [], // ActiveSessionDto[]
      lastFetchedAt: null,
    },
    // Takeover modal state — flipped open by the 409 handler on POST /packages when the
    // server rejects a fresh upload because ANOTHER session owns the current byte-transfer
    // row. Cleared by closeUploadConflict (Not now) or claimUploadSession.fulfilled (Take
    // over here). The attempted upload's rawPayload lives in the module-scoped
    // pendingAttemptedUpload above, not in Redux (File refs aren't serializable).
    uploadConflict: {
      open: false,
      conflict: null, // { existingUploadId, existingStatus, ownerSessionId, existingPackageName, currentSessionId }
    },
    isRetryUploading: false,
    // Live queue snapshot from ChunkedUploadService (bug 2026-08-05). Kept
    // separate from Redux `status` so a queued upload's visual state
    // ("waiting for its turn") doesn't get overwritten when pauseInFlight
    // flips its telemetry to PAUSED. The service fires uploadQueueEvents
    // whenever activePackageId / queueWaiters change; store.js subscribes
    // at boot and dispatches setUploadQueueState.
    uploadQueueState: {
      activeUploadId: null,
      queuedUploadIds: [],
    },
    packageDetails: undefined,
    fetchDetails: {
      status: 'idle', // idle | loading | succeeded | failed
      error: null,
    },
    // uploadingPackages: [
    //   { uploadId: '1', status: UploadClientStatus.UPLOADING, files: {}, uploadMetadata: { packageName: 'Test upload 1' } },
    //   { uploadId: '2', status: UploadClientStatus.UPLOADING, progress: 10, remainingTime: 4215, files: {}, uploadMetadata: { packageName: 'Test upload 2' } },
    //   { uploadId: '3', status: UploadClientStatus.UPLOADING, progress: 90, remainingTime: 15, files: {}, uploadMetadata: { packageName: 'Test upload 3' } },
    //   { uploadId: '4', status: UploadClientStatus.COMPLETED, progress: 100, remainingTime: 0, files: {}, uploadMetadata: { packageName: 'Test upload 4' } },
    //   { uploadId: '5', status: UploadClientStatus.FAILED, progress: 99, remainingTime: 0, files: {}, uploadMetadata: { packageName: 'Test upload 5' } },
    //   { uploadId: '6', status: UploadClientStatus.CANCELED, progress: 99, remainingTime: 0, files: {}, uploadMetadata: { packageName: 'Test upload 6' } },
    // ],
};

const packagesSlice = createSlice({
  name: 'packages',
  initialState: initialPackagesState,
  reducers: {
    setIsProgressDropdownOpen: (state, action) => {
      state.isProgressDropdownOpen = action.payload;
    },
    createUploadingPackage: (state, action) => {
      const payload = {
        ...action.payload,
        status: UploadClientStatus.UPLOADING,
        statusMessage: null,
        verificationAttempted: false,
        // FIFO order: first upload always first in the queue. Selectors sort by this asc.
        createdAt: action.payload.createdAt || Date.now(),
      };
      state.uploadingPackages = [payload, ...state.uploadingPackages]
    },
    // Surfaces a rehydrated package whose bytes are all server-side already but whose backend
    // status hasn't reached a truly-terminal state yet. The user sees a "Server is processing
    // your upload…" card with no resume controls (there's nothing for them to resume — the
    // bytes are gone from the local File object anyway). The boot-time watcher updates this
    // entry to COMPLETED or FAILED once the backend's verdict arrives.
    surfacePostBytesProcessing: (state, action) => {
      const record = action.payload;
      if (!record?.uploadId) return;
      const existing = state.uploadingPackages.find((p) => p.uploadId === record.uploadId);
      if (existing && existing.status !== UploadClientStatus.FAILED && existing.status !== UploadClientStatus.VERIFYING) {
        return; // a live or already-terminal entry — don't overwrite
      }
      const totalBytes = record.totalSize || 0;
      const entry = {
        uploadId: record.uploadId,
        status: UploadClientStatus.UPLOADING,
        statusMessage: 'Server is finalizing your upload — this can take a few minutes for large packages.',
        verificationAttempted: false,
        progress: 100,
        remainingTime: 0,
        files: record.files || [],
        rehydrated: true,
        processingOnBackend: true,
        // Wall-clock timestamp for the "is this server-side processing stuck?" warning the
        // card surfaces after a few minutes. Distinct from createdAt (upload start time) and
        // persistedAt (last IDB write) — neither of those reflects when WE started watching.
        processingStartedAt: Date.now(),
        pausedRecord: record,
        createdAt: record.createdAt || record.persistedAt || Date.now(),
        uploadMetadata: {
          packageName: record.packageName || 'Upload',
          uploadId: record.uploadId,
        },
      };
      if (existing) {
        state.uploadingPackages = state.uploadingPackages.map((p) =>
          p.uploadId === record.uploadId ? entry : p
        );
      } else {
        state.uploadingPackages = [entry, ...state.uploadingPackages];
      }
    },
    rehydratePausedPackage: (state, action) => {
      const record = action.payload;
      if (!record?.uploadId) return;
      const existing = state.uploadingPackages.find((p) => p.uploadId === record.uploadId);
      // If a live in-flight session is still present (UPLOADING/PAUSED), leave it alone.
      // Only replace stale terminal-client states (FAILED/VERIFYING/COMPLETED/CANCELED) — those
      // can happen when a laptop sleep exhausts the TUS retry tail and the catch path flips
      // Redux to FAILED while the IndexedDB record stays alive. Without this recovery the
      // in-progress page goes empty until the user reloads the tab.
      if (existing) {
        const recoverableFromStatuses = new Set([
          UploadClientStatus.FAILED,
          UploadClientStatus.VERIFYING,
        ]);
        if (!recoverableFromStatuses.has(existing.status)) return;
      }
      const totalBytes = record.totalSize || 0;
      const uploadedBytes = (record.files || []).reduce(
        (acc, f) => acc + (f.bytesUploaded || 0),
        0
      );
      const progress = totalBytes > 0
        ? Math.min(100, Math.round((uploadedBytes / totalBytes) * 100))
        : 0;
      const rehydratedEntry = {
        uploadId: record.uploadId,
        status: UploadClientStatus.PAUSED,
        statusMessage: null,
        verificationAttempted: false,
        progress,
        remainingTime: 0,
        files: record.files || [],
        rehydrated: true,
        pausedRecord: record,
        // Preserve the original creation order so a rehydrated paused package keeps its
        // position relative to its siblings. Fall back to persistedAt if createdAt is
        // missing (older records pre-FIFO change).
        createdAt: record.createdAt || record.persistedAt || Date.now(),
        // Original contributor attribution (2026-08-06 admin takeover). Null on
        // normal own-user rehydrations; populated when the record came from a
        // backend-only synthesis + hydrate flow after an admin claimed a
        // stranger's upload. The card renders a "for {name}" chip when this
        // uid differs from the current user's uid.
        contributorId: record.contributorId || null,
        contributorName: record.contributorName || null,
        uploadMetadata: {
          packageName: record.packageName || 'Paused upload',
          uploadId: record.uploadId,
        },
      };
      if (existing) {
        state.uploadingPackages = state.uploadingPackages.map((p) =>
          p.uploadId === record.uploadId ? rehydratedEntry : p
        );
      } else {
        state.uploadingPackages = [rehydratedEntry, ...state.uploadingPackages];
      }
    },
    updateUploadingPackage: (state, action) => {
      const { uploadId, ...rest } = action.payload;
      if ([UploadClientStatus.FAILED, UploadClientStatus.VERIFYING].includes(rest.status)) {
        const upload = state.uploadingPackages.find((p) => p.uploadId === uploadId);
        // Guard PAUSED alongside CANCELED: while paused, the tus client's onShouldRetry path
        // can briefly flip per-file state to "retrying" before abort takes effect. Don't let
        // that bubble up to the package status and overwrite the user's pause.
        if (
          upload?.status === UploadClientStatus.CANCELED ||
          upload?.status === UploadClientStatus.PAUSED
        ) {
          return; // No changes
        }
      }
      state.uploadingPackages = state.uploadingPackages.map((p) => p.uploadId === uploadId ? { ...p, ...rest } : p)
    },
    deleteUploadingPackage: (state, action) => {
      state.uploadingPackages = state.uploadingPackages.filter((p) => p.uploadId !== action.payload)
    },
    setPackagesView: (state, action) => {
      state.packagesView = action.payload;
    },
    setRehydrationStatus: (state, action) => {
      state.rehydrationStatus = action.payload;
    },
    openUploadConflict: (state, action) => {
      state.uploadConflict = {
        open: true,
        conflict: action.payload?.conflict || null,
      };
    },
    closeUploadConflict: (state) => {
      state.uploadConflict = { open: false, conflict: null };
      // Also drop the stashed rawPayload so a dismissed dialog truly means "don't queue".
      pendingAttemptedUpload = null;
    },
    setUploadQueueState: (state, action) => {
      // Fired by store.js's subscribeUploadQueue bridge whenever the
      // ChunkedUploadService queue changes. Kept in slice so the UI's
      // useUploadQueueState selector re-renders reactively.
      const payload = action.payload || {};
      state.uploadQueueState = {
        activeUploadId: payload.activeUploadId ?? null,
        queuedUploadIds: Array.isArray(payload.queuedUploadIds)
          ? [...payload.queuedUploadIds]
          : [],
      };
    },
  },
  extraReducers: (builder) =>{
    builder
      // Multi-tenancy leak fix (2026-08-05): reset every user-scoped slice
      // field on sign-out so the next user to sign in on this tab starts
      // from a clean state. Matched by literal action type to avoid a
      // static import cycle between packagesSlice and authSlice (they'd
      // otherwise import each other transitively via the store bootstrap).
      // Companion teardowns run in store.js middleware:
      //   - ChunkedUploadService.abortAll() clears the in-memory sessions
      //   - __resetPackagesModuleStateOnSignOut() clears recentResumeAttempts
      //     and the stashed take-over payload above.
      .addCase('auth/signOut/fulfilled', () => initialPackagesState)
      .addCase(fetchPackages.pending, (state) => {
        state.fetch.status = 'loading';
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.fetch.status = 'succeeded';
        state.packages = (action.payload.content || []).map(enrichUpload);
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements ?? 0;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.fetch.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchPackageDetails.pending, (state) => {
        state.fetchDetails.status = 'loading';
      })
      .addCase(fetchPackageDetails.fulfilled, (state, action) => {
        state.fetchDetails.status = 'succeeded';
        state.packageDetails = action.payload.content;
      })
      .addCase(fetchPackageDetails.rejected, (state, action) => {
        state.fetchDetails.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(retryUploadPackage.pending, (state) => {
        state.isRetryUploading = true;
      })
      .addCase(retryUploadPackage.fulfilled, (state, action) => {
        state.isRetryUploading = false;
      })
      .addCase(retryUploadPackage.rejected, (state, action) => {
        state.isRetryUploading = false;
      })
      .addCase(verifyUploadStatus.fulfilled, (state, action) => {
        const { uploadId, data } = action.payload;
        logUploadClientEvent('verification-success', { uploadId, state: data?.state });
        state.uploadingPackages = state.uploadingPackages.map((upload) => {
          if (upload.uploadId !== uploadId) {
            return upload;
          }
          const mapping = mapVerificationState(data?.state);
          if (mapping.status === UploadClientStatus.FAILED) {
            logUploadClientEvent('verification-result-failed', { uploadId, state: data?.state });
          }
          if (mapping.status === UploadClientStatus.COMPLETED) {
            logUploadClientEvent('verification-result-existing', { uploadId, state: data?.state });
          }
          return {
            ...upload,
            status: mapping.status,
            statusMessage: mapping.message,
            verificationAttempted: true,
          };
        });
      })
      .addCase(fetchActiveSessions.pending, (state) => {
        state.crossDeviceUploads.status = 'loading';
      })
      .addCase(fetchActiveSessions.fulfilled, (state, action) => {
        const items = Array.isArray(action.payload?.uploads) ? action.payload.uploads : [];
        state.crossDeviceUploads = {
          status: 'succeeded',
          currentSessionId: action.payload?.currentSessionId || null,
          items,
          lastFetchedAt: Date.now(),
        };

        // Silent-void guard (2026-08-05 follow-up). If the backend returns an
        // upload the current session owns but there's no matching local card
        // (IDB record missing, cleared, or hidden by hide-migration before
        // adoption fires), CrossDeviceUploadsList intentionally hides it
        // (assumes a local card exists) and the user sees nothing at all.
        // Synthesize a lightweight local PAUSED card so the upload is at
        // least visible; the resume flow may need a re-pick if the IDB
        // manifest was lost, but visibility is the immediate need.
        // Uploads the user cancelled locally but whose /cancel POST hasn't landed
        // yet must NOT be re-surfaced as phantom paused cards. The pending queue
        // owns the intent; the ChunkedUploadService boot drainer retries the POST
        // in the background.
        const synthesized = synthesizeLocalCardsFromBackend(
          items,
          state.uploadingPackages,
          pendingCancelIdSet(),
        );
        for (const synth of synthesized) {
          const totalBytes = synth.totalSize || 0;
          const uploadedBytes = synth.bytesUploaded || 0;
          const progress = totalBytes > 0
            ? Math.min(100, Math.round((uploadedBytes / totalBytes) * 100))
            : 0;
          state.uploadingPackages = [{
            uploadId: synth.uploadId,
            status: UploadClientStatus.PAUSED,
            statusMessage: 'Recovered from server — reselect your files to continue.',
            verificationAttempted: false,
            progress,
            remainingTime: 0,
            files: [],
            rehydrated: true,
            fromBackendOnly: true,
            createdAt: Date.now(),
            contributorId: synth.contributorId,
            contributorName: synth.contributorName,
            uploadMetadata: {
              packageName: synth.packageName,
              uploadId: synth.uploadId,
            },
          }, ...state.uploadingPackages];
        }
      })
      .addCase(fetchActiveSessions.rejected, (state) => {
        state.crossDeviceUploads.status = 'failed';
      })
      .addCase(verifyUploadStatus.rejected, (state, action) => {
        const payloadUploadId = action.payload?.uploadId ?? action.meta.arg;
        logUploadClientEvent('verification-request-failed', {
          uploadId: payloadUploadId,
          message: action.payload?.error?.message ?? action.error?.message,
        });
        state.uploadingPackages = state.uploadingPackages.map((upload) => {
          if (upload.uploadId !== payloadUploadId) {
            return upload;
          }
          if (upload.status !== UploadClientStatus.VERIFYING) {
            return upload;
          }
          return {
            ...upload,
            statusMessage: VERIFICATION_FAILURE_MESSAGE,
            verificationAttempted: true,
          };
        });
      })
  }
});

export const {
  setIsProgressDropdownOpen,
  createUploadingPackage,
  rehydratePausedPackage,
  surfacePostBytesProcessing,
  updateUploadingPackage,
  deleteUploadingPackage,
  setPackagesView,
  setRehydrationStatus,
  openUploadConflict,
  closeUploadConflict,
  setUploadQueueState,
} = packagesSlice.actions;

// Selector: live queue snapshot for driving the "queued" visual state
// (hourglass icon) on the upload card. See UploadStateControl.
export const useUploadQueueState = () =>
  useSelector((state) => state.packages.uploadQueueState, shallowEqual);

// Cross-device state selectors — consumed by CrossDeviceUploadsBanner and any component that
// needs to know whether the current browser owns the visible upload. shallowEqual so that a
// poll returning identical items doesn't churn every consumer's memo / effect deps (same
// reasoning as useActiveUploads above).
export const useCrossDeviceUploads = () =>
  useSelector((state) => state.packages.crossDeviceUploads, shallowEqual);

export const useUploadConflict = () =>
  useSelector((state) => state.packages.uploadConflict, shallowEqual);

export const useIsRehydratingUploads = () =>
  useSelector((state) => state.packages.rehydrationStatus === 'pending');

// "Active" = anything still in motion or paused awaiting user action. Excludes terminal states
// (COMPLETED/FAILED/CANCELED/VERIFYING) which are session-only artefacts. Shared by the popup
// (capped) and the /uploads/in-progress page (full list) so both views agree on what counts.
const ACTIVE_UPLOAD_STATUSES = new Set([
  UploadClientStatus.UPLOADING,
  UploadClientStatus.PAUSED,
]);

export const isActiveUpload = (upload) =>
  !!upload && ACTIVE_UPLOAD_STATUSES.has(upload.status);

// FIFO: first upload is always first in the queue. Packages without createdAt (defensive for
// legacy state) sort to the front as if they were created at time 0. The /uploads/in-progress
// page shows the whole list in this order; the popup picks the head — which is also the active
// uploader, since the queue runs oldest-first.
const sortActiveByCreatedAt = (list) =>
  [...list].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

// shallowEqual: sortActiveByCreatedAt allocates a fresh array every call, so without it every
// unrelated store dispatch (including the 2s cross-device poll) would return a new reference
// and re-render every consumer. With it, identical content → the previous reference is reused,
// which stabilizes downstream useMemo / useEffect deps (notably useHasOwnedActiveUpload's
// interval — otherwise the interval was restarting on every dispatch, tripping React's
// Maximum-update-depth guard on the Active uploads page).
export const useActiveUploads = () =>
  useSelector(
    (state) => sortActiveByCreatedAt(state.packages.uploadingPackages.filter(isActiveUpload)),
    shallowEqual
  );

export const usePackagesState = () => useSelector(state => state.packages);
export const usePackagesActions = () => {
  const dispatch = useDispatch();
  return useMemo(() => ({
    setIsProgressDropdownOpen: (isOpen) => dispatch(setIsProgressDropdownOpen(isOpen)),
    uploadPackage: (metadata) => dispatch(uploadPackage(metadata)),
    cancelUpload: (payload) => dispatch(cancelUpload(payload)),
    pauseUpload: (uploadId) => dispatch(pauseUpload(uploadId)),
    resumeUpload: (uploadId) => dispatch(resumeUpload(uploadId)),
    rehydratedResumeUpload: (payload) => dispatch(rehydratedResumeUpload(payload)),
    autoResumeRehydratedWithHandles: (excludeUploadId) =>
      dispatch(autoResumeRehydratedWithHandles(excludeUploadId)),
    pauseAllUploads: () => dispatch(pauseAllUploads()),
    resumeAllUploads: () => dispatch(resumeAllUploads()),
    retryUploadPackage: (uploadId) => dispatch(retryUploadPackage(uploadId)),
    updateUploadingPackage: (payload) => dispatch(updateUploadingPackage(payload)),
    deleteUploadingPackage: (uploadId) => dispatch(deleteUploadingPackage(uploadId)),
    fetchPackages: (params) => dispatch(fetchPackages(params)),
    setPackagesView: (params) => dispatch(setPackagesView(params)),
    fetchPackageDetails: (uploadMetadataId) => dispatch(fetchPackageDetails(uploadMetadataId)),
    fetchActiveSessions: () => dispatch(fetchActiveSessions()),
    claimUploadSession: (uploadId) => dispatch(claimUploadSession(uploadId)),
    claimAllVisibleUploads: () => dispatch(claimAllVisibleUploads()),
    closeUploadConflict: () => dispatch(closeUploadConflict()),
  }), [dispatch]);
}

export default packagesSlice.reducer;
